// ConversationsSection.tsx
import React, { useState } from "react";
import { ChatCompletionMessageParam } from "openai/resources";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Plus, Trash2, ChevronDown } from "lucide-react";
import { AddConversationModal } from "./AddConversationModal";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  initializeConversation,
  generateAIPhoneAgentResponse,
  generateSimulatedPatientResponse,
} from "@/lib/aiPhoneAgent";

interface SimulatedConversation {
  id: string;
  scenario: string;
  conversation: ChatCompletionMessageParam[];
}

interface ConversationsSectionProps {
  mainSystemPrompt: string;
  strategyPrompt: string;
  callDescription: string;
  simulatedConversations: SimulatedConversation[];
  onAddConversation: (scenario: string) => void;
  onDeleteConversation: (id: string) => void;
  onUpdateConversation: (
    id: string,
    conversation: ChatCompletionMessageParam[]
  ) => void;
  onReset: () => void;
  onLoadingChange?: (loading: boolean) => void;
}

export const ConversationsSection: React.FC<ConversationsSectionProps> = ({
  mainSystemPrompt,
  strategyPrompt,
  callDescription,
  simulatedConversations,
  onAddConversation,
  onDeleteConversation,
  onUpdateConversation,
  onReset,
}) => {
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState<{
    id: string;
    content: string;
    role: "user" | "assistant";
  } | null>(null);
  const [runningSimulations, setRunningSimulations] = useState<{
    [key: string]: boolean;
  }>({});
  const [error, setError] = useState("");
  const [openConversations, setOpenConversations] = useState<{
    [key: string]: boolean;
  }>({});

  const handleSimulateConversation = async (id: string, scenario: string) => {
    try {
      setError("");
      setStreamingMessage(null);
      setRunningSimulations((prev) => ({ ...prev, [id]: true }));
      setOpenConversations((prev) => ({ ...prev, [id]: true }));

      // Initialize conversation with system prompts
      let conversationHistory = await initializeConversation(
        mainSystemPrompt,
        strategyPrompt,
        callDescription
      );

      // Generate initial AI greeting
      const initialAIResponse = await generateAIPhoneAgentResponse(
        conversationHistory,
        strategyPrompt,
        (chunk) => {
          if (chunk.includes("AI:")) {
            setStreamingMessage({
              id,
              content: chunk,
              role: "assistant",
            });
          }
        }
      );

      // Add the generated greeting to conversation history
      conversationHistory.push({
        role: "assistant",
        content: initialAIResponse,
      });
      onUpdateConversation(id, [...conversationHistory]);
      setStreamingMessage(null);

      // Main conversation loop
      for (let turn = 0; turn < 15; turn++) {
        setStreamingMessage(null);

        // Generate patient response
        const patientResponse = await generateSimulatedPatientResponse(
          conversationHistory,
          scenario,
          (chunk) => {
            if (chunk.includes("Patient:")) {
              setStreamingMessage({
                id,
                content: chunk,
                role: "user",
              });
            }
          }
        );

        if (patientResponse.includes("END_CONVERSATION")) {
          conversationHistory.push({
            role: "user",
            content: patientResponse,
          });
          onUpdateConversation(id, [...conversationHistory]);
          break;
        }

        conversationHistory.push({
          role: "user",
          content: patientResponse,
        });
        onUpdateConversation(id, [...conversationHistory]);
        setStreamingMessage(null);

        // Generate AI response
        const aiResponse = await generateAIPhoneAgentResponse(
          conversationHistory,
          strategyPrompt,
          (chunk) => {
            if (chunk.includes("AI:")) {
              setStreamingMessage({
                id,
                content: chunk,
                role: "assistant",
              });
            }
          }
        );

        if (aiResponse.includes("END_CONVERSATION")) {
          conversationHistory.push({
            role: "assistant",
            content: aiResponse,
          });
          onUpdateConversation(id, [...conversationHistory]);
          break;
        }

        conversationHistory.push({
          role: "assistant",
          content: aiResponse,
        });
        onUpdateConversation(id, [...conversationHistory]);
      }
    } catch (error) {
      console.error("Error:", error);
      setError("Failed to simulate conversation. Please try again.");
    } finally {
      setRunningSimulations((prev) => ({ ...prev, [id]: false }));
      setStreamingMessage(null);
    }
  };

  const formatMessage = (content: string) => {
    if (!content) return "No message content";

    const aiMatch = content.match(/^AI:\s*([\s\S]*)/);
    const patientMatch = content.match(/^Patient:\s*([\s\S]*)/);

    if (aiMatch) {
      return aiMatch[1] || "Empty AI message";
    } else if (patientMatch) {
      return patientMatch[1] || "Empty patient message";
    }

    return content;
  };

  return (
    <Card className="mb-4 bg-muted/50">
      <CardHeader>
        <CardTitle className="flex flex-wrap justify-between items-center gap-2 text-lg">
          Simulated Conversations
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => setAddModalOpen(true)}
              variant="outline"
              size="sm"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Custom Scenario
            </Button>
            {simulatedConversations.length > 0 && (
              <Button onClick={onReset} variant="outline" size="sm">
                Reset All
              </Button>
            )}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-4">
          {simulatedConversations.map((conv) => (
            <Collapsible
              key={conv.id}
              open={openConversations[conv.id]}
              onOpenChange={(isOpen) =>
                setOpenConversations((prev) => ({ ...prev, [conv.id]: isOpen }))
              }
              className="bg-card rounded-md border"
            >
              <CollapsibleTrigger className="w-full">
                <div className="flex flex-wrap items-center justify-between gap-2 p-4 hover:bg-muted/50 rounded-t-md">
                  <div className="flex items-center min-w-0">
                    <ChevronDown
                      className={`w-4 h-4 mr-2 transition-transform duration-200 ${
                        openConversations[conv.id] ? "transform rotate-180" : ""
                      }`}
                    />
                    <h3 className="font-semibold text-left break-words">{conv.scenario}</h3>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSimulateConversation(conv.id, conv.scenario);
                      }}
                      size="sm"
                      variant="outline"
                      disabled={runningSimulations[conv.id]}
                    >
                      {runningSimulations[conv.id]
                        ? "Running..."
                        : "Run Simulation"}
                    </Button>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteConversation(conv.id);
                      }}
                      size="sm"
                      variant="outline"
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="p-4 md:p-8 max-h-96 overflow-y-auto border-t text-sm">
                  {conv.conversation.map((msg, index) => {
                    if (msg.role === "system") return null;

                    const content = msg.content as string;
                    const formattedContent = formatMessage(content);

                    return (
                      <div
                        key={index}
                        className={`mb-4 ${
                          msg.role === "assistant"
                            ? "text-info"
                            : "text-success"
                        }`}
                      >
                        <strong>
                          {msg.role === "assistant" ? "AI Agent" : "Patient"}:
                        </strong>{" "}
                        <pre className="whitespace-pre-wrap font-sans">
                          {formattedContent}
                        </pre>
                        {content.includes("END_CONVERSATION") && (
                          <div className="mt-2 text-destructive font-medium">
                            Conversation Ended
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {runningSimulations[conv.id] &&
                    streamingMessage &&
                    streamingMessage.id === conv.id && (
                      <div
                        className={`text-muted-foreground animate-pulse mb-4 ${
                          streamingMessage.role === "assistant"
                            ? "text-info"
                            : "text-success"
                        }`}
                      >
                        <strong>
                          {streamingMessage.role === "assistant"
                            ? "AI Agent"
                            : "Patient"}
                          :
                        </strong>{" "}
                        <pre className="whitespace-pre-wrap font-sans">
                          {formatMessage(streamingMessage.content)}
                        </pre>
                      </div>
                    )}
                </div>
              </CollapsibleContent>
            </Collapsible>
          ))}
        </div>

        <AddConversationModal
          open={addModalOpen}
          onOpenChange={setAddModalOpen}
          onAdd={onAddConversation}
        />
      </CardContent>
    </Card>
  );
};
